import { FacilityDropdownInput } from "#src/batteries-included-components/Dropdowns";
import { FilterArea } from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useDeviceTypes from "#src/components/hooks/useDeviceTypes";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import {
  Accordion,
  DateSelectorInput,
  DropdownInput,
  TextInput,
  TextInputBare,
  useSearchDOM,
  type StorageKeys,
} from "@validereinc/common-components";
import { AssetType } from "@validereinc/domain";
import { Devices } from "@validereinc/domain-controllers";
import React, { useState } from "react";

const { DeviceStatusOptions } = Devices.view;

export const DevicesViewFilterArea = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { localize } = useLocalization();

  return (
    <FilterArea.Root<{
      period: { from: Date; to: Date };
    }>
      storageKey={viewConfigStorageKey}
      defaultValues={{
        period: DEFAULT_DATE_RANGES.currentWholeMonth,
      }}
    >
      <FilterArea.Container
        aria-label={`View Filters for ${localize(`${AssetType.DEVICE}_plural`)}`}
      >
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <DateSelectorInput
                name={"period"}
                variant="month"
                label="Period"
                isInline
                isLabelShown={false}
                onChange={(val) => handleOnChange(val, "period")}
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

export const DevicesTableFilterAreaContent = ({
  hasCustomAttributeFilters,
}: {
  hasCustomAttributeFilters?: boolean;
}) => {
  const { localize } = useLocalization();
  const { customAttributeFilters: deviceCustomAttrFilters } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.DEVICE,
      enabled: hasCustomAttributeFilters,
    });
  const [deviceTypes] = useDeviceTypes();
  const [searchQuery, setSearchQuery] = useState("");
  const { onContainerRefChange } = useSearchDOM({
    searchQuery,
    containerQuery: "[role='region'] > div:first-child",
    searchByQueries: [
      { type: "element-text", query: "label" },
      { type: "attribute", query: "placeholder" },
    ],
  });

  return (
    <div ref={onContainerRefChange}>
      <TextInputBare
        type="search"
        name="static-search"
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search for a filter..."
        style={{ marginBottom: 16 }}
      />
      <Accordion defaultActiveKeys={[AssetType.DEVICE]}>
        <Accordion.AccordionPanel
          dataKey={AssetType.DEVICE}
          title={localize(`${AssetType.DEVICE}_plural`)}
        >
          <DropdownInput
            label={`${localize(AssetType.DEVICE)} Type`}
            placeholder="Select Type..."
            name="type_id"
            labelKey="name"
            valueKey="id"
            options={deviceTypes}
            isFluid
            isMulti
            isSearchable
            isOptionalTextShown={false}
          />
          <DropdownInput
            label="Status"
            name="status"
            options={DeviceStatusOptions}
            labelKey="label"
            valueKey="value"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown={false}
          />
          {hasCustomAttributeFilters ? deviceCustomAttrFilters : null}
        </Accordion.AccordionPanel>
        <Accordion.AccordionPanel
          dataKey={AssetType.FACILITY}
          title={localize(`${AssetType.FACILITY}_plural`)}
        >
          <FacilityDropdownInput
            name="facility.id"
            placeholder={`Select ${localize(`${AssetType.FACILITY}_plural`)}...`}
            isMulti
            isFluid
            isOptionalTextShown={false}
          />
        </Accordion.AccordionPanel>
      </Accordion>
    </div>
  );
};

export const DevicesTableFilterArea = ({
  filterConfigStorageKey,
  hasCustomAttributeFilters = true,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  hasCustomAttributeFilters?: boolean;
}) => {
  const { localize } = useLocalization();

  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container
        aria-label={`Filters for ${localize(`${AssetType.DEVICE}_plural`)}`}
      >
        <FilterDrawer.Root>
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div style={{ marginRight: 8, marginBottom: 0 }}>
                <TextInput
                  name="name"
                  label="Search"
                  isLabelShown={false}
                  placeholder={`Search ${localize(`${AssetType.DEVICE}_plural`)}...`}
                  type="search"
                  isInline
                  onChange={(val) => handleOnChange(val, "name")}
                />
              </div>
            )}
          </FilterArea.Content>
          <FilterDrawer.Trigger />
          <FilterDrawer.Content>
            <DevicesTableFilterAreaContent
              hasCustomAttributeFilters={hasCustomAttributeFilters}
            />
          </FilterDrawer.Content>
        </FilterDrawer.Root>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
